import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Target, Code, Brain, Cloud, Database, Shield, Lock, Bot, Sparkles, Eye, Mic, TrendingUp, Scale, GraduationCap, Blocks, Utensils } from 'lucide-react';
import { useBanner } from '../contexts/BannerContext';

interface ServiceItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  color: string;
}

export const ServicesDropdown: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isBannerVisible, bannerHeight } = useBanner();
  const location = useLocation();

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    // Add event listeners when dropdown is hovered
    if (isHovered) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isHovered]);

  // Show dropdown on hover only
  const shouldShowDropdown = isHovered;

  // Close dropdown and reset states when navigating
  const handleServiceClick = () => {
    setIsHovered(false);
  };

  // Helper function to close menu and reset all states
  const closeMenu = () => {
    setIsHovered(false);
  };

  const services: ServiceItem[] = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Cognitive Reach",
      description: "AI-powered digital marketing solutions",
      href: "/services/cognitive-reach",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Cognitive Train",
      description: "AI training & consulting services",
      href: "/services/cognitive-train",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cognitive Cloud",
      description: "Scalable cloud infrastructure",
      href: "/services/cognitive-cloud",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Cognitive Build", 
      description: "Custom software development",
      href: "/services/cognitive-build",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Cognitive Intelligence",
      description: "AI & data analytics solutions",
      href: "/services/cognitive-intelligence", 
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Cognitive Agents",
      description: "AI automation & RPA solutions",
      href: "/services/cognitive-agents",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Cognitive Generate",
      description: "Generative AI & content creation",
      href: "/services/cognitive-generate",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Cognitive Vision",
      description: "Computer vision & image analytics",
      href: "/services/cognitive-vision",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "Cognitive Voice",
      description: "Conversational AI & voice solutions",
      href: "/services/cognitive-voice",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Cognitive Predict",
      description: "Predictive analytics & forecasting",
      href: "/services/cognitive-predict",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Cognitive IoT",
      description: "Internet of things solutions",
      href: "/services/cognitive-iot",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Cognitive Secure",
      description: "Advanced cybersecurity solutions",
      href: "/services/cognitive-secure",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Utensils className="w-6 h-6" />,
      title: "Cognitive Robotics",
      description: "Robotics implementation & consulting",
      href: "/services/cognitive-robotics",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Cognitive Compliance",
      description: "Regulatory & legal AI solutions",
      href: "/services/cognitive-compliance",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: <Blocks className="w-6 h-6" />,
      title: "Cognitive Ledger",
      description: "Blockchain & distributed ledger technology",
      href: "/services/cognitive-ledger",
      color: "text-blue-600 dark:text-blue-400"
    }
  ];

  return (
    <div 
      ref={dropdownRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to="/services"
        className={`relative font-medium transition-all duration-300 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 ${
          location.pathname === "/services" || location.pathname.startsWith("/services/") ? "text-blue-600 dark:text-blue-400" : ""
        }`}
      >
        Services
        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 dark:bg-blue-400 transition-all duration-300 hover:w-full"></span>
      </Link>


      {/* Dropdown Menu */}
      <div 
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ease-out ${
          shouldShowDropdown 
            ? 'opacity-100 visible transform translate-y-0 scale-100' 
            : 'opacity-0 invisible transform -translate-y-2 scale-98'
        }`}
        style={{ top: isBannerVisible ? `${bannerHeight + 23.5}px` : '64px' }}
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden transition-all duration-300 ease-out ${
            shouldShowDropdown ? 'transform translate-y-0 opacity-100' : 'transform -translate-y-1 opacity-90'
          }`}>
            {/* Services Grid - Responsive Layout */}
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {services.map((service, index) => (
                  <Link
                    key={index}
                    to={service.href}
                    className="group relative p-4 sm:p-6 bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-2xl hover:scale-105 transition-all duration-200 ease-out"
                    onClick={handleServiceClick}
                    style={{
                      transitionDelay: shouldShowDropdown ? `${index * 30}ms` : '0ms'
                    }}
                  >
                    <div className="relative">
                      <div className={`${service.color} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200`}>
                        {service.icon}
                      </div>
                      <h4 className="text-gray-900 dark:text-white font-semibold mb-2 text-sm sm:text-base group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                        {service.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200">
                        {service.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};